import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { CHECK_IN_COLORS } from './colors';
import { formatCheckInTimestamp, formatOpensAtLabel } from './format';
import { ResultKind } from './types';

interface CheckInResultFloodProps {
  kind: ResultKind;
  audience: 'organizer' | 'attendee';
  firstName?: string;
  lastName?: string;
  email?: string;
  ticketNumber?: string;
  ticketType?: string;
  checkedInAt?: string;
  opensAt?: string;
  onScanNext?: () => void;
  onClose?: () => void;
  onConfirmGuest?: () => void;
  onDeclineGuest?: () => void;
  onShowMyCode?: () => void;
  confirming?: boolean;
}

type IconKind = 'success' | 'error' | 'person' | 'schedule';

const screenFor = (
  kind: ResultKind,
  audience: 'organizer' | 'attendee',
  checkedInAt?: string,
  opensAt?: string,
): { title: string; subline: string; footer: string | null; bg: string; icon: IconKind } => {
  const when = formatCheckInTimestamp(checkedInAt);
  const opens = formatOpensAtLabel(opensAt);
  const organizer = audience === 'organizer';

  switch (kind) {
    case 'checked_in':
      return {
        title: 'Checked In',
        subline: when,
        footer: organizer
          ? 'Scan the next attendee when ready.'
          : "You're all set. Enjoy the event.",
        bg: CHECK_IN_COLORS.green,
        icon: 'success',
      };
    case 'already_checked_in':
      return {
        title: 'Already Checked In',
        subline: when ? `Checked in ${when}` : '',
        footer: organizer
          ? 'This code was already used for this event.'
          : "You're already checked in for this event.",
        bg: CHECK_IN_COLORS.red,
        icon: 'error',
      };
    case 'not_open':
      return {
        title: 'Not Open Yet',
        subline: opens
          ? `Check-in opens at ${opens}.`
          : 'Check-in is not open yet.',
        footer: null,
        bg: CHECK_IN_COLORS.navy,
        icon: 'schedule',
      };
    case 'closed':
      return {
        title: 'Check-in Closed',
        subline: 'Check-in for this event has ended.',
        footer: null,
        bg: CHECK_IN_COLORS.red,
        icon: 'error',
      };
    case 'rsvp_required':
      return {
        title: organizer ? 'Not on RSVP List' : 'RSVP Required',
        subline: organizer
          ? 'This attendee is not on the RSVP list.'
          : 'RSVP for this event before checking in.',
        footer: null,
        bg: CHECK_IN_COLORS.red,
        icon: 'error',
      };
    case 'outside_guest':
      return {
        title: 'Outside Guest',
        subline: 'Not a school email. Confirm to check them in.',
        footer: null,
        bg: CHECK_IN_COLORS.amber,
        icon: 'person',
      };
    case 'see_organizer':
      return {
        title: 'See an Organizer',
        subline: "You're checking in as an outside guest.",
        footer: null,
        bg: CHECK_IN_COLORS.amber,
        icon: 'person',
      };
    case 'self_blocked':
      return {
        title: "Can't check yourself in",
        subline:
          "You can't check yourself in while checking in attendees.",
        footer: null,
        bg: CHECK_IN_COLORS.red,
        icon: 'error',
      };
    case 'unverified':
      return {
        title: 'Account Not Verified',
        subline: 'Only verified accounts can be checked in.',
        footer: null,
        bg: CHECK_IN_COLORS.red,
        icon: 'error',
      };
    case 'cancelled':
      return {
        title: 'This event was cancelled',
        subline: 'Check-in is not available.',
        footer: null,
        bg: CHECK_IN_COLORS.red,
        icon: 'error',
      };
    case 'rate_limited':
      return {
        title: 'Too many attempts',
        subline: 'Wait a moment and try again.',
        footer: null,
        bg: CHECK_IN_COLORS.red,
        icon: 'error',
      };
    default:
      return {
        title: 'Invalid QR Code',
        subline: organizer
          ? ''
          : "This QR code isn't for this event.",
        footer: organizer
          ? 'Ask the attendee to open their code for this event.'
          : null,
        bg: CHECK_IN_COLORS.red,
        icon: 'error',
      };
  }
};

const FloodButton: React.FC<{
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  variant?: 'gold' | 'outline';
  disabled?: boolean;
}> = ({ label, onClick, icon, variant = 'gold', disabled }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      width: '100%',
      maxWidth: '350px',
      height: '48px',
      minHeight: '48px',
      backgroundColor:
        variant === 'gold' ? CHECK_IN_COLORS.gold : 'transparent',
      border:
        variant === 'outline'
          ? `1px solid ${CHECK_IN_COLORS.white}`
          : 'none',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.7 : 1,
      flexShrink: 0,
    }}
  >
    {icon}
    <Typography
      sx={{
        fontFamily: 'Poppins, sans-serif',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '20px',
        color:
          variant === 'gold' ? CHECK_IN_COLORS.goldText : CHECK_IN_COLORS.white,
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const CheckInResultFlood: React.FC<CheckInResultFloodProps> = ({
  kind,
  audience,
  firstName,
  lastName,
  email,
  ticketNumber,
  ticketType,
  checkedInAt,
  opensAt,
  onScanNext,
  onClose,
  onConfirmGuest,
  onDeclineGuest,
  onShowMyCode,
  confirming,
}) => {
  const screen = screenFor(kind, audience, checkedInAt, opensAt);
  const name = [firstName, lastName].filter(Boolean).join(' ');
  const showIdentity = kind !== 'invalid' && kind !== 'rate_limited' && !!name;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        backgroundColor: screen.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '18px',
        pt: '32px',
        px: '20px',
        pb: '24px',
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
            alignItems: 'center',
            pb: '4px',
            width: '100%',
            textAlign: 'center',
            color: CHECK_IN_COLORS.white,
            visibility: showIdentity ? 'visible' : 'hidden',
            opacity: showIdentity ? 1 : 0,
          }}
        >
          {name ? (
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '17px',
                lineHeight: '22px',
                width: '100%',
              }}
            >
              {name}
            </Typography>
          ) : null}
          {email ? (
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '19px',
                width: '100%',
              }}
            >
              {email}
            </Typography>
          ) : null}
          {ticketNumber ? (
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '19px',
                width: '100%',
              }}
            >
              {ticketNumber}
            </Typography>
          ) : null}
          {ticketType ? (
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '19px',
                width: '100%',
              }}
            >
              {ticketType}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            position: 'relative',
            width: '184px',
            height: '184px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '9999px',
              backgroundColor: 'rgba(255,255,255,0.12)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: '26px',
              borderRadius: '9999px',
              backgroundColor: 'rgba(255,255,255,0.16)',
            }}
          />
          {screen.icon === 'success' ? (
            <CheckCircleIcon
              sx={{ fontSize: 88, color: CHECK_IN_COLORS.white, zIndex: 1 }}
            />
          ) : screen.icon === 'person' ? (
            <PersonOutlineIcon
              sx={{ fontSize: 88, color: CHECK_IN_COLORS.white, zIndex: 1 }}
            />
          ) : screen.icon === 'schedule' ? (
            <ScheduleIcon
              sx={{ fontSize: 88, color: CHECK_IN_COLORS.white, zIndex: 1 }}
            />
          ) : (
            <ErrorOutlineIcon
              sx={{ fontSize: 88, color: CHECK_IN_COLORS.white, zIndex: 1 }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'center',
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              fontSize: '30px',
              lineHeight: '38px',
              color: CHECK_IN_COLORS.white,
              textAlign: 'center',
            }}
          >
            {screen.title}
          </Typography>
          {screen.subline ? (
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '21px',
                color: CHECK_IN_COLORS.white85,
              }}
            >
              {screen.subline}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {screen.footer ? (
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 400,
            fontSize: '12.5px',
            lineHeight: '19px',
            color: CHECK_IN_COLORS.white80,
            textAlign: 'center',
          }}
        >
          {screen.footer}
        </Typography>
      ) : null}

      <Box
        sx={{
          mt: '8px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        {kind === 'outside_guest' && audience === 'organizer' ? (
          <>
            <FloodButton
              label="Check in guest"
              onClick={onConfirmGuest}
              disabled={confirming}
            />
            <FloodButton
              label="Don't check in"
              variant="outline"
              onClick={onDeclineGuest}
            />
          </>
        ) : null}

        {kind === 'see_organizer' && audience === 'attendee' ? (
          <>
            <FloodButton label="Show My Code" onClick={onShowMyCode} />
            <FloodButton label="Close" variant="outline" onClick={onClose} />
          </>
        ) : null}

        {audience === 'organizer' &&
        kind !== 'outside_guest' &&
        onScanNext ? (
          <FloodButton
            label="Scan Next"
            onClick={onScanNext}
            icon={
              <QrCodeScannerIcon
                sx={{ fontSize: 20, color: CHECK_IN_COLORS.goldText }}
              />
            }
          />
        ) : null}

        {audience === 'attendee' &&
        kind !== 'checked_in' &&
        kind !== 'already_checked_in' &&
        kind !== 'see_organizer' &&
        onClose ? (
          <FloodButton label="Close" onClick={onClose} />
        ) : null}
      </Box>
    </Box>
  );
};
